/**
 * The overview's plain-language lines, keyed role → domain → scope (`site` = on their sites,
 * `project` = on their projects, `org` = across the organisation).
 *
 * Context: approved with the PLE-1415 design by the CPTO and the procurement team (6 October 2026).
 * Each line summarises exactly the capabilities the rollup rule in `use-role-overview.ts` groups
 * under the same key, and `use-role-overview.test.ts` pins those capabilities, so a permission change fails CI until
 * the line here is reread.
 */
export default {
  ORG_OWN: {
    bankDraws: {
      org: 'View bank draws and claim sheets',
    },
    boq: {
      org: 'View and export cost and selling BOQs, and view BOQ templates and client variations',
    },
    cashflow: {
      org: 'View and export project and site cashflow',
    },
    compliance: {
      org: 'View site compliance and templates, and download documents',
    },
    dashboards: {
      org: 'View the organisation dashboard',
    },
    iam: {
      org: 'View the organisation and its users',
    },
    orders: {
      org: 'View, export and share orders',
    },
    paymentsPrsAps: {
      org: 'View payments, payment requests, proof of payment and all accounts payable records',
    },
    projects: {
      org: 'View projects and their budget summaries',
    },
    returns: {
      org: 'View returns, credits and refunds',
    },
    sites: {
      org: 'View sites and project budget summaries',
    },
    vendorManagement: {
      org: 'View organisation vendors and items, and BOQ vendors',
    },
  },
  ORG_ADMIN: {
    bankDraws: {
      org: 'Generate claim sheets, update draw amounts and schedules, and view bank draws',
    },
    boq: {
      org: 'Create, edit, submit, approve, reject and delete BOQs, their items and BOQ templates, manage BOQ variations, create, edit and cancel client variations and log their payments, and view and export cost and selling BOQs',
    },
    cashflow: {
      org: 'View and export project and site cashflow, and update overheads and project overheads',
    },
    compliance: {
      org: 'Upload, download and remove documents, change site dates, and view site compliance and templates',
    },
    dashboards: {
      org: 'View the organisation dashboard and change the financial year',
    },
    iam: {
      org: 'Edit the organisation, and view and manage its users',
    },
    orders: {
      org: 'Create, approve, reject and cancel orders and order variations, manage carts and order documents, log deliveries, and view, export and share orders',
    },
    paymentsPrsAps: {
      org: 'Manage accounts payable, generate, share and delete AP statements, and view payments, payment requests, proof of payment and all accounts payable records',
    },
    projects: {
      org: 'Create, edit, archive and delete projects, manage project users, and view projects and their budget summaries',
    },
    returns: {
      org: 'Create, approve, reject and cancel returns, and view returns, credits and refunds',
    },
    sites: {
      org: 'Create and edit sites, manage site users, and view sites and project budget summaries',
    },
    vendorManagement: {
      org: 'Add custom vendors, edit and delete organisation vendors, create, edit, delete and bulk upload organisation items, sync vendors, and view BOQ vendors',
    },
  },
  ORG_FO: {
    bankDraws: {
      org: 'Generate claim sheets, update draw amounts, and view bank draws',
    },
    boq: {
      org: 'View and export cost and selling BOQs, view BOQ templates and client variations, and log client variation payments',
    },
    cashflow: {
      org: 'View and export project and site cashflow, and update overheads and project overheads',
    },
    compliance: {
      org: 'View site compliance and templates, and download documents',
    },
    dashboards: {
      org: 'View the organisation dashboard and change the financial year',
    },
    iam: {
      org: 'View and edit the organisation, and view its users',
    },
    orders: {
      org: 'View, export and share orders',
    },
    paymentsPrsAps: {
      org: 'Approve, update and push back payment requests, create and log payments including ad hoc payments, upload and remove PR documents, generate, share and delete AP statements, and view all payment and accounts payable records',
    },
    projects: {
      org: 'View projects and their budget summaries',
    },
    returns: {
      org: 'Complete or cancel credits and refunds, and view returns, credits and refunds',
    },
    sites: {
      org: 'View sites and project budget summaries',
    },
    vendorManagement: {
      org: 'View organisation vendors and items, and BOQ vendors and items',
    },
  },
  ORG_FA: {
    bankDraws: {
      org: 'Generate claim sheets, update draw amounts, and view bank draws',
    },
    boq: {
      org: 'View and export cost and selling BOQs, view BOQ templates and client variations, and log client variation payments',
    },
    cashflow: {
      org: 'View and export project and site cashflow, and update overheads and project overheads',
    },
    compliance: {
      org: 'View site compliance and templates, and download documents',
    },
    dashboards: {
      org: 'View the organisation dashboard',
    },
    iam: {
      org: 'View the organisation and its users',
    },
    orders: {
      org: 'View, export and share orders',
    },
    paymentsPrsAps: {
      org: 'Update payment requests, create and log payments including ad hoc payments, upload and remove PR documents, generate, share and delete AP statements, and view all payment and accounts payable records',
    },
    projects: {
      org: 'View projects and their budget summaries',
    },
    returns: {
      org: 'Complete or cancel credits and refunds, and view returns, credits and refunds',
    },
    sites: {
      org: 'View sites and project budget summaries',
    },
    vendorManagement: {
      org: 'View organisation vendors and items, and BOQ vendors and items',
    },
  },
  PROJ_MNGR: {
    bankDraws: {
      project: 'Generate claim sheets, update draw amounts and schedules, and view bank draws',
    },
    boq: {
      project:
        'Create, edit, submit, approve, reject and delete BOQs and their items, add BOQ templates, manage BOQ variations, create, edit and cancel client variations and log their payments, and view and export cost and selling BOQs',
      org: 'View BOQ templates',
    },
    cashflow: {
      project: 'View and export project and site cashflow, and update overheads',
    },
    compliance: {
      project: 'Upload, download and remove documents, change site dates, and view site compliance',
      org: 'View compliance templates',
    },
    iam: {
      org: 'View the organisation and its users',
    },
    orders: {
      project:
        'Create, approve, reject and cancel orders and order variations, manage carts and order documents, log deliveries, and view, export and share orders',
    },
    paymentsPrsAps: {
      project: 'View payment requests and their totals, and upload or remove their documents',
      org: 'View payments and proof of payment',
    },
    projects: {
      project:
        'Edit, archive and delete projects, manage project users, and view projects and their budget summaries',
      org: 'See the list of projects in the organisation',
    },
    returns: {
      project: 'Create, approve, reject and cancel returns, and view returns, credits and refunds',
    },
    sites: {
      project: 'Create and edit sites, manage site users, and view sites',
      org: 'View project budget summaries',
    },
    vendorManagement: {
      org: 'Add custom vendors, edit and delete organisation vendors, create, edit, delete and bulk upload organisation items, sync vendors, and view BOQ vendors',
    },
  },
  PROJ_PROF: {
    bankDraws: {
      project: 'View bank draws',
    },
    boq: {
      project: 'Approve or deny client variations, and view client variations and the selling BOQ',
    },
    dashboards: {
      org: 'View the client dashboard',
    },
    iam: {
      org: 'View the organisation',
    },
    projects: {
      project: 'View these projects',
    },
    sites: {
      project: 'View sites and project budget summaries',
    },
  },
  PROJ_PROC_OFF: {
    bankDraws: {
      project: 'Generate claim sheets, update draw amounts and schedules, and view bank draws',
    },
    boq: {
      project:
        'Create, edit, submit, approve, reject and delete BOQs and their items, add BOQ templates, add, approve and reject BOQ variations, create, edit and cancel client variations and log their payments, and view and export cost and selling BOQs',
      org: 'View BOQ templates',
    },
    cashflow: {
      project: 'View and export project and site cashflow, and update overheads',
    },
    compliance: {
      project: 'Upload, download and remove documents, and view site compliance',
      org: 'View compliance templates',
    },
    iam: {
      org: 'View the organisation and its users',
    },
    orders: {
      project:
        'Create, approve, reject and cancel orders and order variations, manage carts and order documents, log deliveries, and view, export and share orders',
      org: 'See the list of orders in the organisation',
    },
    paymentsPrsAps: {
      project: 'View payment requests and their totals',
      org: 'See the list of payment requests in the organisation',
    },
    projects: {
      project: 'View projects and their budget summaries',
      org: 'See the list of projects in the organisation',
    },
    returns: {
      project:
        'Create, edit, approve, reject and cancel returns, and view returns, credits and refunds',
      org: 'See the lists of returns, credits and refunds in the organisation',
    },
    sites: {
      project: 'View sites and project budget summaries',
    },
    vendorManagement: {
      org: 'Add custom vendors, edit and delete organisation vendors, create, edit, delete and bulk upload organisation items, sync vendors, and view BOQ vendors',
    },
  },
  PROJ_CLI_BASE: {
    boq: {
      project: 'View client variations',
    },
    dashboards: {
      org: 'View the client dashboard',
    },
  },
  PROJ_CLI_APRV: {
    boq: {
      project: 'View, approve and deny client variations',
    },
    dashboards: {
      org: 'View the client dashboard',
    },
  },
  SITE_ADMIN: {
    bankDraws: {
      site: 'Generate claim sheets and view bank draws',
    },
    boq: {
      site: 'Create, submit, approve, reject and delete BOQs, edit their items, add BOQ templates and BOQ variations, create, edit and cancel client variations and log their payments, and view and export cost and selling BOQs',
      org: 'View BOQ templates',
    },
    cashflow: {
      site: 'View and export site cashflow, and update overheads',
    },
    compliance: {
      site: 'Upload, download and remove documents, change site dates, and view site compliance',
      org: 'View compliance templates',
    },
    iam: {
      org: 'View the organisation and its users',
    },
    orders: {
      site: 'View the cart, view, export and share orders, upload and remove order documents, and log deliveries',
    },
    paymentsPrsAps: {
      site: 'View payment requests and their totals',
      org: 'View payments and proof of payment',
    },
    projects: {
      site: 'View the projects these sites belong to',
      org: 'See the list of projects in the organisation',
    },
    returns: {
      site: 'View returns, credits and refunds',
    },
    sites: {
      site: 'Edit sites, manage site users, and view sites',
    },
    vendorManagement: {
      org: 'Add custom vendors, delete organisation vendors, create, edit, delete and bulk upload organisation items, sync vendors, and view BOQ vendors',
    },
  },
  SITE_PROC_OFF: {
    bankDraws: {
      site: 'Generate claim sheets and view bank draws',
    },
    boq: {
      site: 'Create, edit, submit, approve, reject and delete BOQs and their items, add BOQ templates, add, approve and reject BOQ variations, create, edit and cancel client variations and log their payments, and view and export cost and selling BOQs',
      org: 'View BOQ templates',
    },
    cashflow: {
      site: 'View and export site cashflow, and update overheads',
    },
    compliance: {
      site: 'Download documents and view site compliance',
      org: 'View compliance templates',
    },
    iam: {
      org: 'View the organisation and its users',
    },
    orders: {
      site: 'Create, approve, reject and cancel orders and order variations, manage carts and order documents, log deliveries, and view, export and share orders',
    },
    paymentsPrsAps: {
      site: 'View payment requests and their totals, and upload or remove their documents',
      org: 'View payments and proof of payment',
    },
    projects: {
      site: 'View the projects these sites belong to',
      org: 'See the list of projects in the organisation',
    },
    returns: {
      site: 'Create, approve, reject and cancel returns, and view returns, credits and refunds',
    },
    sites: {
      site: 'View sites',
    },
    vendorManagement: {
      org: 'Add custom vendors, edit organisation vendors, create, edit, delete and bulk upload organisation items, sync vendors, and view BOQ vendors',
    },
  },
  SITE_FIN_OFF: {
    bankDraws: {
      site: 'Generate claim sheets and view bank draws',
    },
    boq: {
      site: 'Approve or reject BOQs, view cost and selling BOQs and client variations, and log client variation payments',
      org: 'View BOQ templates',
    },
    cashflow: {
      site: 'View and export site cashflow, and update overheads',
    },
    compliance: {
      site: 'Upload, download and remove documents, and view site compliance',
      org: 'View compliance templates',
    },
    iam: {
      org: 'View the organisation and its users',
    },
    orders: {
      site: 'View the cart, view, export and share orders, upload and remove order documents, and log deliveries',
    },
    paymentsPrsAps: {
      site: 'Verify and update payment requests, upload or remove their documents, and view payment requests and their totals',
      org: 'View payments and proof of payment',
    },
    projects: {
      site: 'View the projects these sites belong to',
      org: 'See the list of projects in the organisation',
    },
    returns: {
      site: 'Create returns, complete credits and refunds, and view returns, credits and refunds',
    },
    sites: {
      site: 'View sites',
    },
    vendorManagement: {
      org: 'View organisation vendors and items, and BOQ vendors',
    },
  },
  SITE_FLD_USR: {
    boq: {
      site: 'Create, submit and delete BOQs, edit their items, add BOQ templates and BOQ variations, view client variations, and view and export cost and selling BOQs',
      org: 'View BOQ templates',
    },
    cashflow: {
      site: 'View and export site cashflow, and update overheads',
    },
    compliance: {
      site: 'View site compliance',
      org: 'View compliance templates',
    },
    iam: {
      org: 'View the organisation and its users',
    },
    orders: {
      site: 'Create orders, manage carts and order documents, log deliveries, and view, export and share orders',
    },
    projects: {
      site: 'View the projects these sites belong to',
      org: 'See the list of projects in the organisation',
    },
    returns: {
      site: 'View returns',
    },
    sites: {
      site: 'View sites',
    },
    vendorManagement: {
      org: 'Edit organisation items, and view organisation vendors and items, and BOQ vendors',
    },
  },
  SITE_PROF: {
    bankDraws: {
      site: 'View bank draws',
    },
    boq: {
      site: 'Approve or deny client variations, and view client variations and the selling BOQ',
    },
    dashboards: {
      org: 'View the client dashboard',
    },
    iam: {
      org: 'View the organisation',
    },
    projects: {
      site: 'View the projects these sites belong to',
      org: 'See the list of projects in the organisation',
    },
    sites: {
      site: 'View sites',
      org: 'View project budget summaries',
    },
  },
  SITE_CLI_BASE: {
    boq: {
      site: 'View client variations',
    },
    dashboards: {
      org: 'View the client dashboard',
    },
  },
  SITE_CLI_APRV: {
    boq: {
      site: 'View, approve and deny client variations',
    },
    dashboards: {
      org: 'View the client dashboard',
    },
  },
};
